import React, { useEffect, useState } from "react"
import { db } from "../../utils/firebase"
import { NavLink } from "react-router-dom"
import moment from "moment"
import "./booking.scss"

/**
 * Functional component to return the enquiry table row
 */
export default function Booking({ id, details, maskDetails }) {
	const [agent, setAgent] = useState({})
	const [isRepeatClient, setIsRepeatClient] = useState(false)

	/**
	 * Generate a string to represent the date_from date from the timestamps
	 */
	const dateFrom = moment(details.date_from?._seconds, "X").format("MMM D[,] YYYY")
	const dateBooked = moment(details.created?._seconds, "X").format("MMM D[,] YYYY HH:mm")

	/**
	 * Determine if this is a direct (GHD-Direct) booking based on the
	 * fields stamped onto the booking when it's created via the direct flow
	 */
	const isDirectBooking =
		typeof details?.direct_booking_details?.id !== "undefined" ||
		!!details?.direct_booking_id ||
		details?.booking_type === "DIRECT"

	/**
	 * Build the site label, mirroring the logic used on the
	 * margin attribution dashboard
	 */
	let siteLabel
	if (isDirectBooking) {
		siteLabel = "GHD-DIRECT"
	} else if (details.enquiry?.site === "SYSTEM" && isRepeatClient) {
		siteLabel = "SYSTEM-REPEAT"
	} else {
		siteLabel = details.enquiry?.site || "GHD-DIRECT"
	}

	/**
	 * On component load
	 */
	useEffect(() => {
		/**
		 * Get the agent record from the database
		 */
		db.doc(`users/${details.agent}`)
			.get()
			.then((agentDoc) => {
				/**
				 * Set the agent data into the local state
				 */
				setAgent(agentDoc.data())
			})
	}, [])

	/**
	 * For SYSTEM bookings, check whether this client has more than one
	 * booking on file so we can flag it as a SYSTEM-REPEAT, matching
	 * the margin attribution behaviour
	 */
	useEffect(() => {
		if (isDirectBooking) return
		if (details.enquiry?.site !== "SYSTEM") return
		if (!details.client) return

		db.collection("bookings")
			.where("client", "==", details.client)
			.limit(2)
			.get()
			.then((snap) => setIsRepeatClient(snap.size > 1))
			.catch(() => setIsRepeatClient(false))
	}, [details.client, details.enquiry?.site, isDirectBooking])

	const totalCost =
		details.margins?.customer_price ??
		(details.booking_type === "DIRECT" ? details?.total_cost : details.enquiry?.quoted) ??
		0

	const margin = details.margins?.profit ?? 0

	const formatCurrency = (value) =>
		Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })

	const marginPercent = Number(totalCost) > 0 ? ((Number(margin) / Number(totalCost)) * 100).toFixed(1) : null

	return (
		<tr>
			<td>{siteLabel}</td>
			<td>{dateBooked}</td>
			<td>
				{agent?.first_name && `${agent.first_name} ${agent.last_name}`}
				{!agent?.first_name && <small className="no-value">No agent assigned</small>}
			</td>
			<td>
				{!maskDetails && (
					<NavLink to={`client/${details.client}`}>
						{details.first_name && `${details.first_name} ${details.last_name}`}
						{!details.first_name && <small className="no-value">No name set</small>}
					</NavLink>
				)}

				{maskDetails && (
					<>
						{details.first_name && `${details.first_name} ${details.last_name}`}
						{!details.first_name && <small className="no-value">No name set</small>}
					</>
				)}
			</td>
			<td>{details.booked_location ? details.booked_location : details.enquiry?.location}</td>
			<td>{dateFrom}</td>
			<td>£{formatCurrency(totalCost)}</td>
			<td>
				£{formatCurrency(margin)}
				{marginPercent !== null && <small className="no-value"> ({marginPercent}%)</small>}
			</td>
			<td className="is-button">
				<NavLink to={`booking/${id}`}>View booking</NavLink>
			</td>
		</tr>
	)
}
